import React, { useRef, useState, useMemo, useEffect, useLayoutEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Chip, Divider, Slider,
  Select, MenuItem, IconButton, FormControl, Tooltip, Dialog,
} from '@mui/material';
import TimelineIcon        from '@mui/icons-material/Timeline';
import PersonIcon          from '@mui/icons-material/Person';
import GroupsIcon          from '@mui/icons-material/Groups';
import CalendarIcon        from '@mui/icons-material/CalendarToday';
import LayersIcon          from '@mui/icons-material/Layers';
import ReplayIcon          from '@mui/icons-material/Replay';
import ChevronLeftIcon     from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon    from '@mui/icons-material/ChevronRight';
import FullscreenIcon      from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon  from '@mui/icons-material/FullscreenExit';
import celfinetLogo from '../../../assets/Cyient_Slate_Std_RGB.png';

// ── Layout ────────────────────────────────────────────────────────────────────
const ROW_H      = 48;
const ACT_W      = 180;
const TEAM_W     = 100;
const TECH_W     = 50;
const VENDOR_W   = 80;
const LABEL_W    = ACT_W + TEAM_W + TECH_W + VENDOR_W;
const HEADER_H   = 56;   // altura total do cabeçalho
const HEADER_ROW1 = 26;  // altura da linha superior (mês/ano)
const BAR_R      = 5;
const BAR_PAD    = 10;
const MIN_BAR    = 8;
const MIN_SVG_H  = 220;
const BODY_MAX_H = 480;  // altura máxima da área de linhas (com scroll)

// ── Paleta ────────────────────────────────────────────────────────────────────
const FEATURE_COLOR = {
  'Feature':                    '#4E79A7',
  'Optimisation':               '#59A14F',
  'Parameter Workbook Rollout': '#E07B39',
  'Software Upgrade':           '#8B6BB1',
  'Service Protection Period':  '#E15759',
  'Maintenance':                '#e1df57',
  'FOA':                		    '#FF6F00',
	'Design/Documentation':       '#00BCD4',
};
const DEFAULT_BAR = '#9BA3AF';

// ── Helpers ───────────────────────────────────────────────────────────────────
const parseDate = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return new Date(`${slash[3]}-${slash[1]}-${slash[2]}T12:00:00Z`);
  const dash = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dash) {
    const [, y, a, b] = dash;
    const iso = Number(a) > 12 ? `${y}-${b}-${a}` : `${y}-${a}-${b}`;
    return new Date(`${iso}T12:00:00Z`);
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const fmt = (d) => {
  if (!d) return null;
  const y  = d.getUTCFullYear();
  const m  = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const buildTicks = (minMs, maxMs, pxPerDay) => {
  const monthMarkers = [];

  // ── Linha superior: sempre meses ─────────────────────────────────────────
  const mc = new Date(minMs);
  mc.setUTCDate(1);
  while (mc.getTime() <= maxMs + 86400000 * 15) {
    monthMarkers.push(new Date(mc));
    mc.setUTCMonth(mc.getUTCMonth() + 1);
  }

  // ── Linha inferior: granularidade adaptativa ──────────────────────────────
  // pxPerDay >= 14 → dias individuais
  // pxPerDay >=  4 → dias fixos (1, 8, 15, 22 de cada mês)
  // pxPerDay <   4 → sem fine ticks
  let step;
  if      (pxPerDay >= 14) step = 'day';
  else if (pxPerDay >=  4) step = 'week';
  else                      step = 'none';

  const ticks = [];
  if (step === 'day') {
    const cur = new Date(minMs);
    while (cur.getTime() <= maxMs) {
      ticks.push({ date: new Date(cur), step: 'day' });
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
  } else if (step === 'week') {
    const cur = new Date(minMs);
    cur.setUTCDate(1);
    while (cur.getTime() <= maxMs + 86400000 * 7) {
      for (const d of [1, 8, 15, 22]) {
        const t = new Date(cur);
        t.setUTCDate(d);
        if (t.getTime() >= minMs && t.getTime() <= maxMs) {
          ticks.push({ date: t, step: 'week' });
        }
      }
      cur.setUTCMonth(cur.getUTCMonth() + 1);
    }
  }

  return { ticks, monthMarkers, step };
};

const fmtMonth = (date) =>
  date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });

const fmtTick = (date, step) => {
  if (step === 'month') return fmtMonth(date);
  if (step === 'week')  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
  return String(date.getUTCDate()).padStart(2, '0');
};

// ── Tooltip rico — viewport-aware ─────────────────────────────────────────────
const RichTooltip = ({ entry, mouseX, mouseY, featureColorMap }) => {
  const barColor  = featureColorMap[entry.feature] ?? DEFAULT_BAR;
  const startDate = fmt(parseDate(entry.startDate));
  const endDate   = fmt(parseDate(entry.endDate));
  const phases    = (entry.phases ?? []).filter((p) => !p.label?.toLowerCase().startsWith('rollback'));
  const rollbacks = (entry.phases ?? []).filter((p) =>  p.label?.toLowerCase().startsWith('rollback'));

  const TIP_W = 280;
  const TIP_H = 320; // estimativa conservadora da altura máxima
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Horizontal: aparece à direita; flip para esquerda se sair do ecrã
  const left = mouseX + 18 + TIP_W > vw ? mouseX - TIP_W - 12 : mouseX + 18;
  // Vertical: sobe se ficar perto do fundo
  const top  = mouseY + TIP_H > vh     ? Math.max(8, mouseY - TIP_H + 16) : mouseY - 16;

  return (
    <Box sx={{
      position: 'fixed', left, top,
      zIndex: 9999, pointerEvents: 'none', width: TIP_W,
      bgcolor: 'background.paper', borderRadius: 2,
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      border: '1px solid', borderColor: 'divider', overflow: 'hidden',
    }}>
      <Box sx={{ px: 1.75, py: 1.25, bgcolor: barColor, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'white', lineHeight: 1.3, flex: 1 }}>
          {entry.title}
        </Typography>
        {entry.technology && (
          <Chip label={entry.technology} size="small" sx={{
            height: 18, fontSize: '0.65rem', fontWeight: 800,
            bgcolor: 'rgba(255,255,255,0.25)', color: 'white', flexShrink: 0,
          }} />
        )}
      </Box>

      <Box sx={{ px: 1.75, py: 1.25 }}>
        {(entry.vendor || entry.feature) && (
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 1 }}>
            {[entry.vendor, entry.feature].filter(Boolean).join(' · ')}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          <CalendarIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
          <Typography sx={{ fontSize: '0.74rem', color: 'text.primary' }}>
            {startDate ?? '—'}
            <Box component="span" sx={{ mx: 0.5, color: 'text.disabled' }}>→</Box>
            {endDate ?? <Box component="span" sx={{ color: 'warning.main', fontStyle: 'italic' }}>ongoing</Box>}
          </Typography>
        </Box>

        {entry.teamName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
            <GroupsIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
            <Typography sx={{ fontSize: '0.74rem' }}>{entry.teamName}</Typography>
          </Box>
        )}

        {entry.ownerNames?.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.75 }}>
            <PersonIcon sx={{ fontSize: 13, color: 'text.disabled', mt: '2px' }} />
            <Typography sx={{ fontSize: '0.74rem', lineHeight: 1.5 }}>
              {entry.ownerNames.join(', ')}
            </Typography>
          </Box>
        )}

        {phases.length > 0 && (
          <>
            <Divider sx={{ my: 0.75 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <LayersIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Phases</Typography>
            </Box>
            {phases.map((p, idx) => (
              <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3, pl: 0.5 }}>
                <Typography sx={{ fontSize: '0.72rem' }}>
                  {p.label ?? `Phase ${idx + 1}`}{p.crq ? ` — ${p.crq}` : ''}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', ml: 1, flexShrink: 0 }}>
                  {fmt(parseDate(p.date)) ?? '—'}
                </Typography>
              </Box>
            ))}
          </>
        )}

        {rollbacks.length > 0 && (
          <>
            <Divider sx={{ my: 0.75 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <ReplayIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Rollbacks</Typography>
            </Box>
            {rollbacks.map((r, idx) => (
              <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3, pl: 0.5 }}>
                <Typography sx={{ fontSize: '0.72rem' }}>
                  {r.label ?? `Rollback ${idx + 1}`}{r.crq ? ` — ${r.crq}` : ''}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', ml: 1, flexShrink: 0 }}>
                  {fmt(parseDate(r.date)) ?? '—'}
                </Typography>
              </Box>
            ))}
          </>
        )}

        {entry.comments && (
          <>
            <Divider sx={{ my: 0.75 }} />
            <Typography sx={{ fontSize: '0.71rem', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.4 }}>
              {entry.comments.length > 120 ? entry.comments.slice(0, 120) + '…' : entry.comments}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

// ── Componente principal ──────────────────────────────────────────────────────
const ROWS_OPTIONS = [5, 10, 20, 'All'];

const TrackerGantt = ({ entries, onRowClick, showClosed = false, featureTypes = [], legend = null }) => {
  // Uma única área com scroll (H + V). O cabeçalho e a coluna de labels
  // (Activities/Team/Tech/Vendor) ficam "congelados" via CSS position:sticky
  // dentro DESTE MESMO contentor — não há dois scrolls sincronizados por
  // JavaScript, é sempre o mesmo scroll a mover tudo.
  const scrollRef       = useRef(null);
  const [tooltip,     setTooltip]    = useState(null);
  const [sliderRange, setSliderRange] = useState([0, 100]);
  const [containerW,  setContainerW] = useState(() => Math.max((window?.innerWidth ?? 1200) - 260, 600));
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isMaximized, setIsMaximized] = useState(false);

  // Mapa de cores: começa com o FEATURE_COLOR hardcoded (fallback) e
  // sobrepõe com as cores dinâmicas definidas em Tracker Settings > Type
  // (igual à mesma lógica já usada no TrackerCalendar).
  const featureColorMap = useMemo(() => {
    const map = { ...FEATURE_COLOR };
    (featureTypes ?? []).forEach((f) => { if (f.name && f.color) map[f.name] = f.color; });
    return map;
  }, [featureTypes]);

  useLayoutEffect(() => {
    const measure = () => {
      const el = scrollRef.current;
      if (!el) return;
      // Força o Box a colapsar para medir a largura disponível real,
      // ignorando o conteúdo interno que pode estar a expandir o contentor.
      const prev = el.style.width;
      el.style.width = '0px';
      const parent = el.parentElement;
      const w = parent ? parent.getBoundingClientRect().width : 0;
      el.style.width = prev; // repor
      if (w > 100) setContainerW(w);
    };

    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    ro.observe(document.documentElement);

    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, []);

  const allRows = useMemo(
    () => entries.filter((e) => {
      if (!parseDate(e.startDate)) return false;
      if (!showClosed && (e.status ?? 'Open') === 'Closed') return false;
      return true;
    }),
    [entries, showClosed]
  );

  // Reset página quando os dados mudam
  useEffect(() => { setPage(0); }, [allRows.length]);

  // Paginação
  const totalRows = allRows.length;
  const pageRows  = rowsPerPage === 'All' ? totalRows : rowsPerPage;
  const pageCount = rowsPerPage === 'All' ? 1 : Math.ceil(totalRows / rowsPerPage);
  const rows      = rowsPerPage === 'All'
    ? allRows
    : allRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // ── Limites temporais baseados em TODOS os dados (não só a página)
  const { sliderMin, sliderMax } = useMemo(() => {
    if (!allRows.length) return { sliderMin: Date.now(), sliderMax: Date.now() + 86400000 * 30 };
    const starts = allRows.map((e) => parseDate(e.startDate)?.getTime()).filter(Boolean);
    const ends   = allRows.map((e) => parseDate(e.endDate) ? parseDate(e.endDate).getTime() + 86400000 : null).filter(Boolean);
    const rawMin = Math.min(...starts);
    const rawMax = ends.length ? Math.max(...ends) : Date.now();
    return {
      sliderMin: rawMin - 86400000 * 5,
      sliderMax: rawMax + 86400000 * 10,
    };
  }, [allRows]);

  useEffect(() => { setSliderRange([0, 100]); }, [sliderMin, sliderMax]);

  const totalMs = sliderMax - sliderMin;
  const minMs   = sliderMin + (sliderRange[0] / 100) * totalMs;
  const maxMs   = sliderMin + (sliderRange[1] / 100) * totalMs;

  const sliderFmt = (pct) => {
    const d = new Date(sliderMin + (pct / 100) * totalMs);
    return fmt(d);
  };

  // ── Dimensões
  // A coluna de labels (LABEL_W) e a timeline (chartW) são dois elementos
  // lado a lado dentro do MESMO contentor com scroll — por isso o espaço
  // disponível para a timeline continua a subtrair LABEL_W, tal como
  // sempre foi. As coordenadas x das barras/ticks (msToX) são relativas a
  // 0 (início da timeline), já que a timeline é o seu próprio <svg>,
  // separado do <svg> das labels.
  const MIN_PX_DAY  = 3;
  const chartAvail  = Math.max(containerW - LABEL_W, 200);
  const visibleDays = (maxMs - minMs) / 86400000;
  const pxPerDay    = Math.max(chartAvail / visibleDays, MIN_PX_DAY);
  const chartW      = Math.round(visibleDays * pxPerDay);
  const bodyH       = rows.length * ROW_H + 8;
  const svgH        = Math.max(HEADER_H + bodyH, MIN_SVG_H);
  const msToX       = useCallback((ms) => ((ms - minMs) / (maxMs - minMs)) * chartW, [minMs, maxMs, chartW]);
  const { ticks, monthMarkers, step: tickStep } = useMemo(() => buildTicks(minMs, maxMs, pxPerDay), [minMs, maxMs, pxPerDay]);

  // Auto-scroll para TODAY
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const todayX = msToX(Date.now());
    el.scrollLeft = Math.max(0, todayX - el.clientWidth * 0.33);
  }, [msToX]);

  if (!allRows.length) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">
            No activities with a start date to display in the Gantt chart.
          </Typography>
        </Box>
      </Paper>
    );
  }

  const today     = Date.now();
  const todayX    = msToX(today);
  const showToday = today >= minMs && today <= maxMs;

  const Wrapper = isMaximized ? Dialog : Paper;
  const wrapperProps = isMaximized
    ? { fullScreen: true, open: true, onClose: () => setIsMaximized(false),
        PaperProps: { sx: { p: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column' } } }
    : { variant: 'outlined', sx: { borderRadius: 2, mt: 3, overflow: 'hidden' } };

  return (
    <>
      <Wrapper {...wrapperProps}>

        {/* ── Cabeçalho ────────────────────────────────────────────────── */}
        <Box sx={{
          px: 2.5, py: 1.5,
          display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: 'action.hover',
          flexShrink: 0,
        }}>
          <TimelineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={700} letterSpacing={-0.2}>
            Gantt Chart
          </Typography>
          <Chip
            label={`${totalRows} ${totalRows === 1 ? 'activity' : 'activities'}`}
            size="small"
            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, ml: 0.5 }}
          />
          <Box sx={{ flex: 1 }} />

          {/* Slider de intervalo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 320 }}>
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
              {sliderFmt(sliderRange[0])}
            </Typography>
            <Slider
              value={sliderRange}
              onChange={(_, v) => setSliderRange(v)}
              size="small"
              disableSwap
              sx={{ flex: 1, mx: 0.5 }}
            />
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
              {sliderFmt(sliderRange[1])}
            </Typography>
          </Box>

          <Tooltip title={isMaximized ? 'Restore chart' : 'Maximize chart'}>
            <IconButton size="small" onClick={() => setIsMaximized((v) => !v)}>
              {isMaximized ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

        </Box>

        {/* Legenda partilhada — só aparece aqui em modo fullscreen, porque
            na vista normal ela já está visível na página, por cima do Gantt
            (não faz sentido duplicá-la quando não está maximizado). */}
        {isMaximized && legend && (
          <Box sx={{ px: 2.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            {legend}
          </Box>
        )}

        {/* ── Wrapper só para a marca d'água ficar centrada na zona visível
            (fora da área com scroll — ver nota mais abaixo, junto à Box). ── */}
        <Box sx={{
          position: 'relative',
          ...(isMaximized ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
        }}>
          {/* Marca d'água — fora do contentor com scroll, para não "desaparecer"
              a meio de todo o conteúdo quando há muitas linhas (ex: Rows: All);
              fica sempre centrada no que está visível, independentemente do
              scroll ou da paginação. */}
          <Box sx={{
            position: 'absolute',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none', zIndex: 0,
            width: '30%', minWidth: '200px', opacity: 0.05,
            display: 'flex', justifyContent: 'center'
          }}>
            <Box component="img" src={celfinetLogo} alt="" sx={{ width: '100%' }} />
          </Box>

          {/* ── ÚNICA área de scroll (H + V) ──────────────────────────────
              Cabeçalho: position:sticky + top:0 → fica sempre visível ao
              scrollar verticalmente.
              Coluna de labels: position:sticky + left:0 → fica sempre
              visível ao scrollar horizontalmente.
              Canto (labels do cabeçalho): sticky nos dois eixos ao mesmo
              tempo, para nunca desaparecer em nenhuma direção.
              Isto é tudo CSS puro — não há nenhum listener de scroll nem
              sincronização manual entre elementos. ── */}
          <Box
            ref={scrollRef}
            sx={isMaximized
              ? { overflow: 'auto', flex: 1, minHeight: 0, position: 'relative' }
              : { overflow: 'auto', maxHeight: HEADER_H + BODY_MAX_H, position: 'relative' }
            }
          >
            {/* ══ Linha do cabeçalho — sticky top ══ */}
            <Box sx={{ position: 'sticky', top: 0, zIndex: 3, display: 'flex', bgcolor: 'background.paper' }}>
              {/* Canto — sticky left também, para acompanhar os dois scrolls */}
              <Box sx={{
                position: 'sticky', left: 0, zIndex: 4, flexShrink: 0,
                borderBottom: '1px solid', borderRight: '1px solid', borderColor: 'divider',
                bgcolor: '#f9fafb',
              }}>
                <svg width={LABEL_W} height={HEADER_H} style={{ display: 'block', userSelect: 'none' }}>
                  <line x1={ACT_W}                   y1={0} x2={ACT_W}                   y2={HEADER_H} stroke="#e5e7eb" strokeWidth={1} />
                  <line x1={ACT_W + TEAM_W}          y1={0} x2={ACT_W + TEAM_W}          y2={HEADER_H} stroke="#e5e7eb" strokeWidth={1} />
                  <line x1={ACT_W + TEAM_W + TECH_W} y1={0} x2={ACT_W + TEAM_W + TECH_W} y2={HEADER_H} stroke="#e5e7eb" strokeWidth={1} />
                  <text x={8}                            y={HEADER_H / 2 + 4} fontSize={10} fontFamily="Arial" fontWeight="700" fill="#9ca3af" letterSpacing="1">ACTIVITIES</text>
                  <text x={ACT_W + 6}                    y={HEADER_H / 2 + 4} fontSize={10} fontFamily="Arial" fontWeight="700" fill="#9ca3af" letterSpacing="1">TEAM</text>
                  <text x={ACT_W + TEAM_W + 6}           y={HEADER_H / 2 + 4} fontSize={10} fontFamily="Arial" fontWeight="700" fill="#9ca3af" letterSpacing="1">TECH</text>
                  <text x={ACT_W + TEAM_W + TECH_W + 6}  y={HEADER_H / 2 + 4} fontSize={10} fontFamily="Arial" fontWeight="700" fill="#9ca3af" letterSpacing="1">VENDOR</text>
                </svg>
              </Box>

              {/* Header da timeline — scrolla horizontalmente com o resto do conteúdo */}
              <Box sx={{ flexShrink: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
                <svg width={Math.max(chartW, 1)} height={HEADER_H} style={{ display: 'block', userSelect: 'none' }}>
                  {tickStep !== 'none' && (
                    <line x1={0} y1={HEADER_ROW1} x2={chartW} y2={HEADER_ROW1} stroke="#e5e7eb" strokeWidth={1} />
                  )}
                  {monthMarkers.map((date, i) => {
                    const x  = Math.max(msToX(date.getTime()), 0);
                    const nextDate = monthMarkers[i + 1];
                    const x2 = nextDate ? Math.min(msToX(nextDate.getTime()), chartW) : chartW;
                    if (x >= chartW) return null;
                    const w = x2 - x;
                    return (
                      <g key={`hm-${i}`}>
                        {x > 0 && (
                          <line x1={x} y1={0} x2={x} y2={tickStep === 'none' ? HEADER_H : HEADER_ROW1} stroke="#d1d5db" strokeWidth={1} />
                        )}
                        {w > 40 && (
                          <text x={x + 6} y={tickStep === 'none' ? HEADER_H - 10 : HEADER_ROW1 - 7}
                            fontSize={10} fill="#6b7280" fontFamily="Arial" fontWeight="600">
                            {fmtMonth(date)}
                          </text>
                        )}
                      </g>
                    );
                  })}
                  {tickStep !== 'none' && ticks.map((tick, i) => {
                    const x = msToX(tick.date.getTime());
                    if (x < 0 || x > chartW) return null;
                    return (
                      <g key={`ht-${i}`}>
                        <line x1={x} y1={HEADER_ROW1} x2={x} y2={HEADER_H} stroke="#e5e7eb" strokeWidth={1} />
                        <text x={x + 3} y={HEADER_H - 8} fontSize={9} fill="#9ca3af" fontFamily="Arial">
                          {fmtTick(tick.date, tick.step)}
                        </text>
                      </g>
                    );
                  })}
                  {showToday && (
                    <>
                      <rect x={todayX - 18} y={2} width={36} height={16} rx={3} fill="#EF4444" />
                      <text x={todayX} y={13} textAnchor="middle" fill="white" fontSize={9} fontFamily="Arial" fontWeight="bold">TODAY</text>
                    </>
                  )}
                </svg>
              </Box>
            </Box>

            {/* ══ Linhas — coluna de labels sticky left + timeline normal ══ */}
            <Box sx={{ display: 'flex' }}>
              {/* Coluna de labels — sticky left */}
              <Box sx={{
                position: 'sticky', left: 0, zIndex: 2, flexShrink: 0,
                borderRight: '1px solid', borderColor: 'divider',
              }}>
                <svg width={LABEL_W} height={bodyH} style={{ display: 'block', userSelect: 'none' }}>
                  {/* fillOpacity (não fill sólido) — para a marca d'água
                      (por baixo, ver Box "Marca d'água" acima) continuar
                      visível através da zebra striping, como acontece na
                      TrackerTable (essas linhas não pintam fundo opaco). */}
                  {rows.map((_, i) => (
                    <rect key={i} x={0} y={i * ROW_H} width={LABEL_W} height={ROW_H}
                      fill={i % 2 === 0 ? '#ffffff' : '#f9fafb'}
                      fillOpacity={i % 2 === 0 ? 0 : 0.6} />
                  ))}

                  <line x1={ACT_W}                    y1={0} x2={ACT_W}                    y2={bodyH} stroke="#e5e7eb" strokeWidth={1} />
                  <line x1={ACT_W + TEAM_W}           y1={0} x2={ACT_W + TEAM_W}           y2={bodyH} stroke="#e5e7eb" strokeWidth={1} />
                  <line x1={ACT_W + TEAM_W + TECH_W}  y1={0} x2={ACT_W + TEAM_W + TECH_W}  y2={bodyH} stroke="#e5e7eb" strokeWidth={1} />

                  {rows.map((entry, i) => {
                    const y = i * ROW_H;
                    return (
                      <g key={entry.id}>
                        <clipPath id={`cl-act-${i}`}> <rect x={0}                        y={y} width={ACT_W - 8}    height={ROW_H} /></clipPath>
                        <clipPath id={`cl-team-${i}`}><rect x={ACT_W}                    y={y} width={TEAM_W - 6}   height={ROW_H} /></clipPath>
                        <clipPath id={`cl-tech-${i}`}><rect x={ACT_W + TEAM_W}           y={y} width={TECH_W - 4}   height={ROW_H} /></clipPath>
                        <clipPath id={`cl-vnd-${i}`}> <rect x={ACT_W + TEAM_W + TECH_W} y={y} width={VENDOR_W - 6} height={ROW_H} /></clipPath>

                        <text x={8}                             y={y + ROW_H / 2 + 4} fontSize={11} fontFamily="Arial" fill="#374151" clipPath={`url(#cl-act-${i})`}>
                          <title>{entry.title}</title>{entry.title}
                        </text>
                        <text x={ACT_W + 6}                    y={y + ROW_H / 2 + 4} fontSize={10} fontFamily="Arial" fill="#6b7280" clipPath={`url(#cl-team-${i})`}>
                          <title>{entry.teamName}</title>{entry.teamName ?? '—'}
                        </text>
                        <text x={ACT_W + TEAM_W + 6}           y={y + ROW_H / 2 + 4} fontSize={10} fontFamily="Arial" fill="#6b7280" clipPath={`url(#cl-tech-${i})`}>
                          {entry.technology ?? '—'}
                        </text>
                        <text x={ACT_W + TEAM_W + TECH_W + 6}  y={y + ROW_H / 2 + 4} fontSize={10} fontFamily="Arial" fill="#6b7280" clipPath={`url(#cl-vnd-${i})`}>
                          <title>{entry.vendor}</title>{entry.vendor ?? '—'}
                        </text>

                        <line x1={0} y1={y + ROW_H} x2={LABEL_W} y2={y + ROW_H} stroke="#f3f4f6" strokeWidth={1} />
                      </g>
                    );
                  })}
                </svg>
              </Box>

              {/* Timeline — segue o scroll normalmente, em ambos os eixos */}
              <Box sx={{ flexShrink: 0 }}>
                <svg
                  width={Math.max(chartW, 1)}
                  height={bodyH}
                  style={{ display: 'block', userSelect: 'none' }}
                >
                  {/* Zebra — fillOpacity, não fill sólido (mesma razão da
                      SVG das labels acima: deixar a marca d'água ver-se). */}
                  {rows.map((_, i) => (
                    <rect key={i} x={0} y={i * ROW_H} width={chartW} height={ROW_H}
                      fill={i % 2 === 0 ? '#ffffff' : '#f9fafb'}
                      fillOpacity={i % 2 === 0 ? 0 : 0.6} />
                  ))}

                  {/* Grid vertical */}
                  {ticks.map((tick, i) => {
                    const x = msToX(tick.date.getTime());
                    if (x < 0 || x > chartW) return null;
                    return <line key={i} x1={x} y1={0} x2={x} y2={bodyH} stroke="#e5e7eb" strokeWidth={1} />;
                  })}

                  {/* Today */}
                  {showToday && (
                    <>
                      <line x1={todayX} y1={0} x2={todayX} y2={bodyH}
                        stroke="#EF4444" strokeWidth={1.5} strokeDasharray="4 3" />
                      <rect x={todayX - 18} y={2} width={36} height={16} rx={3} fill="#EF4444" />
                      <text x={todayX} y={13} textAnchor="middle"
                        fill="white" fontSize={9} fontFamily="Arial" fontWeight="bold">TODAY</text>
                    </>
                  )}

                  {/* ══ LINHAS (barras) ══ */}
                  {rows.map((entry, i) => {
                    const y        = i * ROW_H;
                    const barColor = featureColorMap[entry.feature] ?? DEFAULT_BAR;
                    const startMs  = parseDate(entry.startDate)?.getTime();
                    const endMs    = parseDate(entry.endDate) ? parseDate(entry.endDate).getTime() + 86400000 : null;
                    const isOpen   = !endMs;

                    const rawX1 = msToX(startMs);
                    const rawX2 = isOpen ? msToX(Date.now()) : Math.max(msToX(endMs), rawX1 + MIN_BAR);
                    const barX1 = Math.max(rawX1, 0);
                    const barX2 = Math.min(rawX2, chartW - 4);
                    const barW  = Math.max(barX2 - barX1, 0);
                    const barY  = y + BAR_PAD;
                    const barH  = ROW_H - BAR_PAD * 2;
                    const midY  = y + ROW_H / 2;
                    const outOfView = rawX2 < 0 || rawX1 > chartW;

                    return (
                      <g key={entry.id}>
                        {!outOfView && barW > 0 && (
                          <>
                            <rect
                              x={barX1} y={barY} width={barW} height={barH}
                              rx={BAR_R} fill={barColor} opacity={0.85}
                              style={{ cursor: 'pointer' }}
                              onMouseEnter={(e) => setTooltip({ entry, mouseX: e.clientX, mouseY: e.clientY })}
                              onMouseMove={(e)  => setTooltip((prev) => prev ? { ...prev, mouseX: e.clientX, mouseY: e.clientY } : prev)}
                              onMouseLeave={()  => setTooltip(null)}
                              onClick={() => onRowClick?.(entry)}
                            />
                            <clipPath id={`cl-bar-${i}`}>
                              <rect x={barX1 + 4} y={barY} width={barW - 8} height={barH} />
                            </clipPath>
                            {barW > 80 && (
                              <text x={barX1 + 8} y={barY + barH / 2 + 4}
                                fontSize={10} fontFamily="Arial" fontWeight="600"
                                fill="white" style={{ pointerEvents: 'none' }}
                                clipPath={`url(#cl-bar-${i})`}>
                                {entry.feature}
                              </text>
                            )}
                            {isOpen && (
                              <polygon
                                points={`${barX2 + 10},${midY} ${barX2},${barY} ${barX2},${barY + barH}`}
                                fill={barColor} opacity={1} style={{ pointerEvents: 'none' }}
                              />
                            )}
                          </>
                        )}

                        <line x1={0} y1={y + ROW_H} x2={chartW} y2={y + ROW_H} stroke="#f3f4f6" strokeWidth={1} />
                      </g>
                    );
                  })}
                </svg>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ── Barra de paginação ────────────────────────────────────────── */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          gap: 1, px: 2, py: 0.75,
          borderTop: '1px solid', borderColor: 'divider',
          bgcolor: 'action.hover',
          flexShrink: 0,
        }}>
          {/* Rows per page */}
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
            Rows:
          </Typography>
          <FormControl size="small" variant="standard" sx={{ minWidth: 48 }}>
            <Select
              value={rowsPerPage}
              onChange={(e) => { setRowsPerPage(e.target.value); setPage(0); }}
              disableUnderline
              sx={{ fontSize: '0.72rem', color: 'text.secondary' }}
            >
              {ROWS_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt} sx={{ fontSize: '0.78rem' }}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* Info */}
          {rowsPerPage !== 'All' && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', minWidth: 80, textAlign: 'center' }}>
              {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, totalRows)} of {totalRows}
            </Typography>
          )}
          {rowsPerPage === 'All' && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
              {totalRows} of {totalRows}
            </Typography>
          )}

          {/* Prev / Next */}
          <IconButton
            size="small" disabled={page === 0 || rowsPerPage === 'All'}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small" disabled={page >= pageCount - 1 || rowsPerPage === 'All'}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>

      </Wrapper>

      {tooltip && (
        <RichTooltip entry={tooltip.entry} mouseX={tooltip.mouseX} mouseY={tooltip.mouseY} featureColorMap={featureColorMap} />
      )}
    </>
  );
};

export default TrackerGantt;